import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getUser, getUserWallet, getRewardDashboard,
  getRewardHistory, withdrawRewardBalance,
} from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import ServiceGate from "../../components/feature/ServiceGate";
import { useServices } from "../../hooks/useServices";
import { useAnalytics } from "../../hooks/useAnalytics";

const APP_NAME = "Easwallet";
const Sk = ({ className = "" }) => <div className={`animate-pulse bg-skeleton rounded-xl ${className}`} />;

// ── Guest screen ───────────────────────────────────────────────
function GuestRewards({ onSignUp }) {
  const perks = [
    { icon: "ri-percent-line",       tone: "success", title: "Bill Cashback",       desc: "Earn 1% back on every airtime, data, electricity and TV bill you pay" },
    { icon: "ri-fire-line",          tone: "warning", title: "Streak Rewards",      desc: "Stay active daily and earn up to ₦1,000 for a 30-day streak" },
    { icon: "ri-team-line",          tone: "info",    title: "Refer & Earn",         desc: "Earn ₦500 when a friend signs up with your link" },
    { icon: "ri-trophy-line",        tone: "warning", title: "First-Action Bonuses", desc: "One-time bonuses for completing KYC, first deposit, first bill, and more" },
  ];
  const steps = [
    { step: "1", text: "Create your free account",      icon: "ri-user-add-line",           tone: "success" },
    { step: "2", text: "Pay a bill or make a trade",    icon: "ri-receipt-line",             tone: "info" },
    { step: "3", text: "Cashback hits your balance",    icon: "ri-hand-coin-line",           tone: "warning" },
    { step: "4", text: "Move rewards to your wallet",   icon: "ri-wallet-3-line",            tone: "purple" },
  ];
  return (
    <div className="px-5 pb-10 space-y-6 pt-5">
      {/* Hero */}
      <div className="rounded-3xl p-6 text-center relative overflow-hidden" style={{ background: "var(--gradient-hero)", boxShadow: "var(--card-top-highlight)" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />
        <div className="relative z-10">
          <div className="w-16 h-16 bg-white/15 rounded-3xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-gift-2-line text-white text-3xl" />
          </div>
          <h2 className="text-white font-bold text-xl mb-2 font-display">
            Earn While You Spend
          </h2>
          <p className="text-white/60 text-sm leading-relaxed mb-5">
            Get cashback on every bill, earn streak bonuses, and invite friends to unlock even more rewards.
          </p>
          <button onClick={onSignUp}
            className="w-full py-3.5 bg-white text-brand-text font-bold text-sm rounded-2xl active:scale-95 transition-all">
            Start Earning Rewards
          </button>
        </div>
      </div>

      {/* Perk cards */}
      <div>
        <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3">Ways to Earn</p>
        <div className="space-y-3">
          {perks.map(p => (
            <div key={p.title} className="flex items-start gap-3 bg-surface rounded-2xl px-4 py-4 border border-border shadow-card">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `var(--color-${p.tone}-light)`, boxShadow: `inset 0 0 0 1px var(--color-${p.tone}-ring)` }}>
                <i className={`${p.icon} text-lg`} style={{ color: `var(--color-${p.tone})` }} />
              </div>
              <div>
                <p className="text-primary text-sm font-bold mb-0.5">{p.title}</p>
                <p className="text-muted text-xs leading-relaxed">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="bg-surface rounded-2xl border border-border shadow-card p-5">
        <p className="text-primary font-bold text-sm mb-4">How it works</p>
        <div className="space-y-3">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm text-on-brand"
                style={{ background: `var(--color-${s.tone})` }}>
                {s.step}
              </div>
              <p className="text-secondary text-sm">{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="space-y-3">
        <button onClick={onSignUp}
          className="w-full py-4 bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2">
          <i className="ri-user-add-line" />Create Free Account
        </button>
        <button onClick={onSignUp}
          className="w-full py-3.5 bg-surface border border-border-strong text-secondary font-semibold text-sm rounded-2xl hover:bg-surface-hover transition-all active:scale-95">
          Sign In
        </button>
      </div>
    </div>
  );
}

// ── Streak bar ─────────────────────────────────────────────────
function StreakBar({ current, longest }) {
  const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - (6 - i));
    return { label: DAY_LABELS[d.getDay()], isToday: i === 6, isFilled: i >= 6 - Math.min(current, 7) + 1 };
  });
  return (
    <div className="bg-surface rounded-2xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-warning-light rounded-xl flex items-center justify-center">
            <i className="ri-fire-line text-warning text-base" />
          </div>
          <div>
            <p className="text-primary font-bold text-sm">Activity Streak</p>
            <p className="text-muted text-xs">Transact daily to keep it going</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-warning font-bold text-xl">{current}🔥</p>
          <p className="text-muted text-[10px]">Best: {longest}</p>
        </div>
      </div>
      <div className="flex justify-between gap-1">
        {days.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
            <div className={`w-full aspect-square rounded-xl flex items-center justify-center transition-all ${
              day.isFilled ? "shadow-card" : day.isToday ? "bg-surface-muted" : "bg-surface-hover"
            }`}
              style={day.isFilled
                ? { background: "var(--color-warning)" }
                : day.isToday
                  ? { boxShadow: "inset 0 0 0 2px var(--color-warning-ring)" }
                  : {}}>
              {day.isFilled && <i className="ri-fire-fill text-on-brand text-xs" />}
            </div>
            <p className={`text-[9px] font-semibold ${day.isToday ? "text-primary" : "text-muted"}`}>{day.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        {[{ days:3, reward:"₦50", done: current>=3 }, { days:7, reward:"₦200", done: current>=7 }, { days:30, reward:"₦1,000", done: current>=30 }].map(m => (
          <div key={m.days} className={`flex-1 text-center px-2 py-2 rounded-xl border text-[10px] font-semibold ${
            m.done ? "bg-success-light border-success-border text-success-text" : "bg-surface-muted border-border text-muted"
          }`}>
            <p className="text-base">{m.done ? "✅" : "🎯"}</p>
            <p>{m.days}-day</p>
            <p className="font-bold">{m.reward}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const MILESTONES = [
  { key: "kycCompleted",     label: "Complete KYC",        reward: "₦200", icon: "ri-shield-user-line",  tone: "success" },
  { key: "firstFiatDeposit", label: "First Naira Deposit", reward: "₦100", icon: "ri-bank-card-line",    tone: "info" },
  { key: "firstBillPayment", label: "First Bill Payment",  reward: "₦100", icon: "ri-receipt-line",      tone: "warning" },
];

function MilestoneList({ milestones, loading }) {
  const completed = MILESTONES.filter(m => milestones[m.key]).length;
  const pct = Math.round((completed / MILESTONES.length) * 100);
  return (
    <div className="bg-surface rounded-2xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-primary font-bold text-sm">First Action Bonuses</p>
          <p className="text-muted text-xs mt-0.5">{completed}/{MILESTONES.length} completed</p>
        </div>
        <span className="text-brand font-bold text-sm">{pct}%</span>
      </div>
      <div className="h-1.5 bg-surface-muted rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-brand rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-2.5">
        {loading ? [...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Sk className="w-9 h-9 rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-1.5"><Sk className="h-3 w-32" /><Sk className="h-2.5 w-20" /></div>
            <Sk className="h-5 w-14 rounded-full" />
          </div>
        )) : MILESTONES.map(m => {
          const done = !!milestones[m.key];
          return (
            <div key={m.key} className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${done ? "" : "opacity-40"}`} style={{ background: `var(--color-${m.tone}-light)` }}>
                <i className={`${m.icon} text-base`} style={{ color: `var(--color-${m.tone})` }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${done ? "text-primary" : "text-muted"}`}>{m.label}</p>
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${done ? "bg-success-light text-success-text" : "bg-surface-muted text-muted"}`}>
                {done ? "✓ " : "+"}{m.reward}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getEventMeta(type) {
  const map = {
    first_kyc:            { icon: "ri-shield-check-line", tone: "success", label: "KYC Bonus" },
    first_bill_payment:   { icon: "ri-receipt-line",      tone: "warning", label: "First Bill Bonus" },
    first_fiat_deposit:   { icon: "ri-bank-card-line",    tone: "info",    label: "Naira Deposit Bonus" },
    cashback_bill:        { icon: "ri-percent-line",      tone: "success", label: "Bill Cashback" },
    cashback_trade:       { icon: "ri-percent-line",      tone: "purple",  label: "Trade Cashback" },
    cashback_spend:       { icon: "ri-hand-coin-line",    tone: "warning", label: "Cashback Used" },
    streak_3day:          { icon: "ri-fire-line",         tone: "warning", label: "3-Day Streak" },
    streak_7day:          { icon: "ri-fire-line",         tone: "danger",  label: "7-Day Streak" },
    streak_30day:         { icon: "ri-trophy-line",       tone: "warning", label: "30-Day Streak" },
    referral_signup:      { icon: "ri-user-add-line",     tone: "info",    label: "Referral Signup" },
    referral_first_trade: { icon: "ri-team-line",         tone: "success", label: "Referral Trade" },
    admin_bonus:          { icon: "ri-gift-line",         tone: "purple",  label: "Admin Bonus" },
  };
  return map[type] || { icon: "ri-star-line", tone: "muted", label: type };
}

// Resolves a tone name to its color/bg pair, mirroring the helper used on
// ProfilePage — "muted" isn't one of the 5 fixed semantic tones, so it
// falls back to the neutral text-muted / surface-muted pair.
function toneVars(tone) {
  if (tone === "muted") {
    return { color: "var(--color-text-muted)", bg: "var(--color-surface-muted)" };
  }
  return { color: `var(--color-${tone})`, bg: `var(--color-${tone}-light)` };
}

export default function RewardsPage() {
  const { services }    = useServices();
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();
  const { track, conversion } = useAnalytics();

  const [user,             setUser]             = useState(null);
  const [rewardData,       setRewardData]       = useState(null);
  const [history,          setHistory]          = useState([]);
  const [pagination,       setPagination]       = useState(null);
  const [activeTab,        setActiveTab]        = useState("overview");
  const [loading,          setLoading]          = useState(true);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [withdrawing,      setWithdrawing]      = useState(false);
  const [historyPage,      setHistoryPage]      = useState(1);

  const { fees } = useTradingConfig({ enabled: isAuthenticated });
  const refReward       = fees.rewardOnReferral || 20;
  const refTrReward       = fees.rewardOnReferralFirstTrade || 200;

  const referralLink = user ? `${window.location.origin}/auth?ref=${user.referralCode}` : "";

  const fetchAll = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [userData, rewardRes] = await Promise.all([ getUser(), getRewardDashboard() ]);
      if (userData?.status) setUser(userData.user);
      if (rewardRes?.status) setRewardData(rewardRes.data);
    } catch (err) {
      toast.error("Failed to load reward data");
    } finally { setLoading(false); }
  }, [isAuthenticated]);

  const fetchHistory = useCallback(async (page = 1) => {
    if (!isAuthenticated) return;
    setHistoryLoading(true);
    try {
      const res = await getRewardHistory(page, 20);
      if (res?.status) {
        setHistory(page === 1 ? res.data : prev => [...prev, ...res.data]);
        setPagination(res.pagination);
        setHistoryPage(page);
      }
    } catch {} finally { setHistoryLoading(false); }
  }, [isAuthenticated]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === "history" && history.length === 0) fetchHistory(1); }, [activeTab]);

  const handleWithdraw = async () => {
    const balance = rewardData?.balance || 0;
    if (balance < 1000) return toast.warn("Minimum withdrawal is ₦1,000");
    setWithdrawing(true);
    try {
      const res = await withdrawRewardBalance();
      if (res.status) { toast.success(`₦${res.data?.amount?.toLocaleString()} moved to your Naira wallet!`); fetchAll(); }
      else toast.error(res.message || "Withdrawal failed");
    } catch (err) { toast.error(err?.response?.data?.message || "Withdrawal failed"); }
    finally { setWithdrawing(false); }
  };

  const copyLink = () => {
  navigator.clipboard.writeText(referralLink).then(() => {
    toast.success("Link copied!");
    conversion.referral();
  });
};
  const copyCode = () => navigator.clipboard.writeText(user?.referralCode || "").then(() => toast.success("Code copied!"));

  const balance      = rewardData?.balance       || 0;
  const totalEarned  = rewardData?.totalEarned   || 0;
  const streak       = rewardData?.currentStreak || 0;
  const longest      = rewardData?.longestStreak || 0;
  const milestones   = rewardData?.milestones    || {};
  const recentEvents = rewardData?.recentEvents  || [];
  const referralEvents = recentEvents.filter(e => e.type === "referral_signup" || e.type === "referral_first_trade");
  const cashbackTotal  = recentEvents.filter(e => e.type === "cashback_bill" || e.type === "cashback_trade").reduce((s, e) => s + e.amount, 0);

  return (
    <ServiceGate serviceKey="rewards" services={services} fullPage>
      <div className="min-h-screen bg-bg pb-28">

        {/* Header */}
        <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Rewards</h1>
              <p className="text-muted text-xs">Earn by transacting, paying bills & inviting friends</p>
            </div>
          </div>

          {/* Tabs — only show when authenticated */}
          {isAuthenticated && (
            <div className="flex mt-4 bg-surface-muted rounded-2xl p-1 gap-1">
              {[{ id: "overview", label: "Overview" }, { id: "history", label: "History" }, { id: "referral", label: "Referrals" }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${activeTab === tab.id ? "bg-surface text-primary shadow-card" : "text-secondary"}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── NON-AUTH: guest rewards screen ────────────────── */}
        {!isAuthenticated ? (
          <GuestRewards onSignUp={() => navigate("/auth")} />
        ) : (
          <>
            {/* ── OVERVIEW TAB ── */}
            {activeTab === "overview" && (
              <div className="px-5 pt-5 space-y-5">
                {/* Balance card */}
                {loading ? (
                  <div className="rounded-3xl p-6" style={{ background: "var(--gradient-hero)" }}>
                    <div className="h-3 w-32 bg-white/20 rounded-full animate-pulse mb-3" />
                    <div className="h-10 w-40 bg-white/20 rounded-xl animate-pulse mb-2" />
                    <div className="h-3 w-24 bg-white/15 rounded-full animate-pulse mb-6" />
                    <div className="h-12 bg-white/15 rounded-2xl animate-pulse" />
                  </div>
                ) : (
                  <div className="rounded-3xl p-6 relative overflow-hidden" style={{ background: "var(--gradient-hero)", boxShadow: "var(--card-top-highlight)" }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />
                    <div className="relative z-10">
                      <p className="text-white/60 text-xs font-medium mb-1">Reward Balance</p>
                      <p className="text-white text-4xl font-bold mb-1 font-display">₦{balance.toLocaleString("en-NG")}</p>
                      <p className="text-white/50 text-xs mb-5">Total earned: <span className="text-white font-semibold">₦{totalEarned.toLocaleString()}</span></p>
                      <button onClick={handleWithdraw} disabled={withdrawing || balance < 1000}
                        className="w-full py-3 rounded-2xl text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{ background: balance >= 1000 ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.07)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }}>
                        {withdrawing
                          ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Moving to wallet…</>
                          : <><i className="ri-arrow-down-circle-line" />{balance >= 1000 ? "Move to Naira Wallet" : `Min ₦1,000 to withdraw`}</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Stats */}
                {loading ? (
                  <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Sk key={i} className="h-20" />)}</div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Day Streak",  value: `${streak}🔥`,                        icon: "ri-fire-line",    tone: "warning" },
                      { label: "Cashback",    value: `₦${cashbackTotal.toLocaleString()}`, icon: "ri-percent-line", tone: "success" },
                      { label: "Referrals",   value: referralEvents.length,                icon: "ri-team-line",    tone: "info" },
                    ].map(stat => (
                      <div key={stat.label} className="bg-surface rounded-2xl border border-border shadow-card p-3 text-center">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2" style={{ background: `var(--color-${stat.tone}-light)`, boxShadow: `inset 0 0 0 1px var(--color-${stat.tone}-ring)` }}>
                          <i className={`${stat.icon} text-base`} style={{ color: `var(--color-${stat.tone})` }} />
                        </div>
                        <p className="text-primary font-bold text-base">{stat.value}</p>
                        <p className="text-muted text-[10px] mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {loading ? <Sk className="h-44" /> : <StreakBar current={streak} longest={longest} />}
                <MilestoneList milestones={milestones} loading={loading} />

                {!loading && recentEvents.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-secondary text-xs font-semibold uppercase tracking-wider">Recent Rewards</p>
                      <button onClick={() => setActiveTab("history")} className="text-brand text-xs font-semibold">See all</button>
                    </div>
                    <div className="bg-surface rounded-2xl border border-border shadow-card overflow-hidden divide-y divide-border">
                      {recentEvents.slice(0, 5).map(event => {
                        const meta = getEventMeta(event.type);
                        const mv = toneVars(meta.tone);
                        const isSpend = event.amount < 0;
                        return (
                          <div key={event._id} className="flex items-center gap-3 px-4 py-3.5">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: mv.bg }}>
                              <i className={`${meta.icon} text-base`} style={{ color: mv.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-primary text-sm font-semibold truncate">{event.description}</p>
                              <p className="text-muted text-xs">{new Date(event.createdAt).toLocaleDateString("en-NG", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}</p>
                            </div>
                            <p className={`font-bold text-sm flex-shrink-0 ${isSpend ? "text-warning" : "text-success"}`}>
                              {isSpend ? "" : "+"}₦{Math.abs(event.amount).toLocaleString()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── HISTORY TAB ── */}
            {activeTab === "history" && (
              <div className="px-5 pt-5 space-y-3">
                <p className="text-secondary text-xs font-semibold uppercase tracking-wider">
                  All Reward Events {pagination ? `(${pagination.total})` : ""}
                </p>
                {historyLoading && history.length === 0 ? (
                  <div className="space-y-2">{[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-surface rounded-2xl border border-border shadow-card px-4 py-3 flex items-center gap-3">
                      <Sk className="w-10 h-10 rounded-xl flex-shrink-0" />
                      <div className="flex-1 space-y-1.5"><Sk className="h-3 w-36" /><Sk className="h-2.5 w-24" /></div>
                      <Sk className="h-3 w-14" />
                    </div>
                  ))}</div>
                ) : history.length === 0 ? (
                  <div className="bg-surface rounded-2xl border border-border shadow-card p-10 text-center">
                    <div className="w-14 h-14 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-3"><i className="ri-gift-line text-muted text-2xl" /></div>
                    <p className="text-secondary font-semibold text-sm mb-1">No rewards yet</p>
                    <p className="text-muted text-xs">Pay a bill or make a trade to start earning</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-surface rounded-2xl border border-border shadow-card overflow-hidden divide-y divide-border">
                      {history.map(event => {
                        const meta = getEventMeta(event.type);
                        const mv = toneVars(meta.tone);
                        const isSpend = event.amount < 0;
                        return (
                          <div key={event._id} className="flex items-center gap-3 px-4 py-3.5">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: mv.bg }}>
                              <i className={`${meta.icon} text-base`} style={{ color: mv.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-primary text-sm font-semibold truncate">{event.description}</p>
                              <p className="text-muted text-xs">{new Date(event.createdAt).toLocaleDateString("en-NG", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`font-bold text-sm ${isSpend ? "text-warning" : "text-success"}`}>
                                {isSpend ? "−" : "+"}₦{Math.abs(event.amount).toLocaleString()}
                              </p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${isSpend ? "bg-warning-light text-warning-text" : "bg-success-light text-success-text"}`}>
                                {isSpend ? "used" : "earned"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {pagination && historyPage < pagination.pages && (
                      <button onClick={() => fetchHistory(historyPage + 1)} disabled={historyLoading}
                        className="w-full py-3 bg-surface border border-border rounded-2xl text-sm font-semibold text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50">
                        {historyLoading ? "Loading…" : "Load more"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── REFERRAL TAB ── */}
            {activeTab === "referral" && (
              <div className="px-5 pt-5 space-y-5">
                {loading ? (
                  <div className="grid grid-cols-2 gap-3"><Sk className="h-20" /><Sk className="h-20" /></div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Friends Referred",  value: referralEvents.filter(e => e.type === "referral_signup").length, icon: "ri-user-add-line", tone: "success" },
                      { label: "Referral Earnings", value: `₦${referralEvents.reduce((s,e) => s+e.amount, 0).toLocaleString()}`, icon: "ri-money-dollar-circle-line", tone: "info" },
                    ].map(stat => (
                      <div key={stat.label} className="bg-surface rounded-2xl border border-border shadow-card p-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `var(--color-${stat.tone}-light)`, boxShadow: `inset 0 0 0 1px var(--color-${stat.tone}-ring)` }}>
                          <i className={`${stat.icon} text-base`} style={{ color: `var(--color-${stat.tone})` }} />
                        </div>
                        <p className="text-primary font-bold text-lg">{stat.value}</p>
                        <p className="text-muted text-xs mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-surface rounded-2xl border border-border shadow-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-brand-light rounded-xl flex items-center justify-center">
                      <i className="ri-links-line text-brand text-base" />
                    </div>
                    <p className="text-primary font-bold text-sm">Your Referral Link</p>
                  </div>
                  {loading ? <div className="space-y-3"><Sk className="h-12" /><Sk className="h-12" /></div> : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-surface-muted rounded-xl px-4 py-3">
                        <div>
                          <p className="text-muted text-xs mb-0.5">Referral Code</p>
                          <p className="text-primary font-bold font-mono tracking-widest text-sm">{user?.referralCode || "—"}</p>
                        </div>
                        <button onClick={copyCode} className="flex items-center gap-1.5 text-xs font-semibold text-brand-text bg-brand-light px-3 py-1.5 rounded-lg hover:opacity-80 transition-opacity">
                          <i className="ri-file-copy-line" /> Copy
                        </button>
                      </div>
                      <div className="bg-surface-muted rounded-xl px-4 py-3">
                        <p className="text-muted text-xs mb-1">Referral Link</p>
                        <p className="text-secondary text-xs font-mono truncate mb-2">{referralLink}</p>
                        <div className="flex gap-2">
                          <button onClick={copyLink} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-text bg-brand-light py-2.5 rounded-lg hover:opacity-80 transition-opacity border border-brand-border">
                            <i className="ri-file-copy-line" /> Copy Link
                          </button>
                          <button onClick={() => {
                            navigator.share?.({ title: `Join ${APP_NAME}`, url: referralLink })
                            .catch(() => copyLink());
                            conversion.referral();
                          }}
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-on-brand bg-brand py-2.5 rounded-lg hover:bg-brand-hover transition-colors">
                            <i className="ri-share-line" /> Share
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-surface rounded-2xl border border-border shadow-card p-5">
                  <p className="text-primary font-bold text-sm mb-4">How referrals work</p>
                  <div className="space-y-4">
                    {[
                      { text: "Share your referral link or code with friends",    icon: "ri-share-box-line",          tone: "success" },
                      { text: "Friend signs up using your link",                   icon: "ri-user-add-line",           tone: "info" },
                      { text: `You earn ${refReward} instantly when they sign up`,         icon: "ri-money-dollar-circle-line", tone: "purple" },
                      { text: `Earn ${refTrReward} when they make their first trasaction`,        icon: "ri-exchange-line",   tone: "warning" },
                      { text: "Move your referral earnings to your bank account",  icon: "ri-wallet-3-line",           tone: "success" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${item.tone}-light)` }}>
                          <i className={`${item.icon} text-base`} style={{ color: `var(--color-${item.tone})` }} />
                        </div>
                        <p className="text-secondary text-sm leading-relaxed pt-1.5">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {referralEvents.length > 0 && (
                  <div>
                    <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3">Referral History ({referralEvents.length})</p>
                    <div className="bg-surface rounded-2xl border border-border shadow-card overflow-hidden divide-y divide-border">
                      {referralEvents.map(event => (
                        <div key={event._id} className="flex items-center gap-3 px-4 py-3.5">
                          <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-brand text-sm" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-primary text-sm font-semibold truncate">{event.description}</p>
                            <p className="text-muted text-xs">{new Date(event.createdAt).toLocaleDateString("en-NG", { day:"numeric", month:"short", year:"numeric" })}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-success font-bold text-sm">+₦{event.amount.toLocaleString()}</p>
                            <span className="text-[10px] font-semibold bg-success-light text-success-text px-2 py-0.5 rounded-full">Credited</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <MobileBottomNav />
      </div>
    </ServiceGate>
  );
}
